/**
 * Day 81.5 P1.1 — Shared Toast component (F3 ToastState consolidation).
 *
 * Unified `kind: 'success' | 'error' | 'info'` 3-state shape replaces 14 named
 * + 2 inline ToastState definitions across the codebase per Phase 5 Tier 2
 * audit F3 + Sol-lock S4 cluster split routing to Day 81.5.
 *
 * Canonical rich pattern per COMPONENT_STANDARDS.md §6 (lifted from
 * BuyingSession.tsx Day 54 Phase 4 inline render): icons + animation +
 * aria-live + data-testid + explicit dismiss button. 9 minimal Toast copies
 * across consumers (Settings/Tasks/Files/etc.) upgrade to this richer pattern
 * for free. e2e contract preserved via `data-testid="toast-{kind}"` hook
 * (scripts/e2e-flows.cjs:L985).
 *
 * 4000ms auto-dismiss + manual dismiss button. Fixed top-right positioning
 * with z-[300] (above modals/overlays per DESIGN_TOKENS).
 */

import { useEffect } from 'react';
import { CheckCircle2, Info, X } from 'lucide-react';
import clsx from 'clsx';

export interface ToastState {
  kind: 'success' | 'error' | 'info';
  message: string;
}

interface ToastProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

export function Toast({ toast, onDismiss }: ToastProps): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`toast-${toast.kind}`}
      className={clsx(
        'fixed top-4 right-4 z-[300] w-80 px-3 py-2.5 rounded-lg border shadow-lg flex items-start gap-2',
        'animate-[slideInFromRight_200ms_ease-out]',
        toast.kind === 'success'
          ? 'bg-success/15 border-success/30 text-success'
          : toast.kind === 'info'
            ? 'bg-info/15 border-info/30 text-info'
            : 'bg-danger/15 border-danger/30 text-danger'
      )}
    >
      {toast.kind === 'success' ? (
        <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
      ) : toast.kind === 'info' ? (
        <Info size={16} className="flex-shrink-0 mt-0.5" />
      ) : (
        <X size={16} className="flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 text-xs">{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 text-ink-muted hover:text-ink-primary"
      >
        <X size={14} />
      </button>
    </div>
  );
}
